import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthScreen from './components/screens/AuthScreen';
import HomeScreen from './components/screens/HomeScreen';
import ConfigScreen from './components/screens/ConfigScreen';
import RegisterTruckScreen from './components/screens/RegisterTruckScreen';
import CreateTicketScreen from './components/screens/CreateTicketScreen';
import TicketsScreen from './components/screens/TicketsScreen';
import StatusIndicator from './components/common/StatusIndicator';
import LoadingSpinner from './components/common/LoadingSpinner';
import { useAuth } from './hooks/useAuth';
import { useDevices } from './hooks/useDevices';
import rg4js from 'raygun4js';

rg4js('apiKey', 'KTKV8S66ulWAlvpRhkaOWg');
rg4js('enableCrashReporting', true);

// OAuth callback component
const AuthCallback = ({ onAuthComplete }) => {
	const { loading, isAuthenticated } = useAuth();

	useEffect(() => {
		if (isAuthenticated && onAuthComplete) {
			onAuthComplete();
		}
	}, [isAuthenticated, onAuthComplete]);

	if (loading) {
		return (
			<div className="screen">
				<LoadingSpinner text="Completing authentication..." />
			</div>
		);
	}

	return null; // Will redirect via useEffect
};

// Main App Content Component
const AppContent = () => {
	const [currentScreen, setCurrentScreen] = useState('auth');
	const { isAuthenticated, user, company, setCompany, logout, loading } = useAuth();
	const { devices, config, setConfig, connectDevice } = useDevices();
	const [deviceCode, setDeviceCode] = useState(null);

	useEffect(() => {
		const getDeviceCode = async () => {
			const code = await window.electronAPI.getDeviceCode();
			setDeviceCode(code);
		};
		getDeviceCode();
	}, []);

	// Truck registration state
	const [truckRegistration, setTruckRegistration] = useState({
		step: 1,
		plateImage: null,
		detectedPlate: '',
		confirmedPlate: '',
		truckNumber: '',
		tareWeight: 0,
		isProcessing: false
	});

	// Ticket creation state
	const [ticketCreation, setTicketCreation] = useState({
		step: 1,
		images: [],
		detectedPlate: '',
		selectedProduct: null,
		selectedCustomer: null,
		selectedProject: null,
		poNumber: '',
		tareWeight: 0,
		currentWeight: 0,
		netWeight: 0,
		isProcessing: false
	});

	const isConfigured = devices.printer.connected && devices.scale.connected && company?.Id;

	// Handle authentication state changes
	useEffect(() => {
		if (isAuthenticated && currentScreen === 'auth') {
			setCurrentScreen('home');
		} else if (!isAuthenticated && currentScreen !== 'auth') {
			setCurrentScreen('auth');
		}
	}, [isAuthenticated, currentScreen]);

	const handleAuthComplete = () => {
		setCurrentScreen('home');
	};

	const handleLogout = async () => {
		await logout();
		setCurrentScreen('auth');
	};

	const handleNavigation = (screen) => {
		setCurrentScreen(screen);
	};

	const renderScreen = () => {
		// Show loading if authentication is in progress
		if (loading && currentScreen === 'auth') {
			return (
				<div className="screen">
					<LoadingSpinner text="Checking authentication..." />
				</div>
			);
		}

		// If not authenticated, show auth screen
		if (!isAuthenticated) {
			return <AuthScreen />;
		}

		// Authenticated screens
		switch (currentScreen) {
			case 'home':
				return (
					<HomeScreen
						devices={devices}
						config={config}
						isConfigured={isConfigured}
						onNavigate={handleNavigation}
						user={user}
						company={company}
						onLogout={handleLogout}
						loading={loading}
					/>
				);
			case 'config':
				return (
					<ConfigScreen
						config={config}
						setConfig={setConfig}
						devices={devices}
						onConnect={connectDevice}
						onNavigate={handleNavigation}
						company={company}
						setCompany={setCompany}
					/>
				);
			case 'register':
				return (
					<RegisterTruckScreen
						state={truckRegistration}
						setState={setTruckRegistration}
						devices={devices}
						onNavigate={handleNavigation}
						config={config}
					/>
				);
			case 'ticket':
				return (
					<CreateTicketScreen
						state={ticketCreation}
						setState={setTicketCreation}
						devices={devices}
						user={user}
						company={company}
						onNavigate={handleNavigation}
						config={config}
						deviceCode={deviceCode}
					/>
				);
			case 'tickets':
				return (
					<TicketsScreen
						onNavigate={handleNavigation}
						user={user}
						company={company}
					/>
				);
			default:
				return (
					<HomeScreen
						devices={devices}
						isConfigured={isConfigured}
						onNavigate={handleNavigation}
					/>
				);
		}
	};

			return (
			<div className="app">
				<main className="main-content">
					{renderScreen()}
				</main>
			</div>
		);
};

function App() {
	return (
		<Router>
			<Routes>
				{/* OAuth callback route */}
				<Route
					path="/auth/callback"
					element={
						<div className="app">
							<header className="header">
								<div className="logo">TruckScale Pro</div>
							</header>
							<main className="main-content">
								<AuthCallback
									onAuthComplete={() => {
										// Redirect to main app after auth
										window.location.href = '/';
									}}
								/>
							</main>
						</div>
					}
				/>

				{/* Main application */}
				<Route path="/*" element={<AppContent />} />
			</Routes>
		</Router>
	);
}

export default App;